import { useEffect, useMemo, useRef, useState } from "react";
import { BAND_TABLE, BASELINE, LEVERS, applyLevers, overallScore, bandFor } from "@/components/powlo/home/exampleScore";
import ScoreBreakdown from "@/components/powlo/home/ScoreBreakdown";

export default function ScoreDemo() {
  const [activeLevers, setActiveLevers] = useState<string[]>([]);
  const baselineScore = overallScore(BASELINE);
  const [animatedScore, setAnimatedScore] = useState(baselineScore);
  const rafRef = useRef<number | null>(null);

  const pillars = useMemo(() => applyLevers(BASELINE, activeLevers), [activeLevers]);
  const target = overallScore(pillars);
  const band = bandFor(target);

  // Animate the headline number toward the target whenever it changes.
  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    function step() {
      let reached = false;
      setAnimatedScore((prev) => {
        if (prev === target) {
          reached = true;
          return prev;
        }
        const next = prev + (target - prev) * 0.2;
        const rounded = Math.abs(target - next) < 0.5 ? target : Math.round(next);
        if (rounded === target) reached = true;
        return rounded;
      });
      if (!reached) {
        rafRef.current = requestAnimationFrame(step);
      }
    }
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target]);

  function toggleLever(key: string) {
    setActiveLevers((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  function showBestSetup() {
    setActiveLevers(LEVERS.map((l) => l.key));
  }

  function reset() {
    setActiveLevers([]);
  }

  return (
    <div className="flex flex-col gap-8 rounded-[32px] border border-border bg-white p-6 sm:p-10">
      <div className="flex flex-col items-center gap-4 text-center">
        <span className="text-xs font-bold tracking-widest text-indigo">POWERED BY POWTENTIAL™</span>
        <div className="flex flex-wrap justify-center gap-2">
          {BAND_TABLE.map((bandRow) => (
            <span key={bandRow.label} className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs font-bold text-ink">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: bandRow.color }} />
              {bandRow.label}
              <span className="font-semibold text-mutedLight">{bandRow.range}</span>
            </span>
          ))}
        </div>
        <div className="relative h-56 w-56">
          <svg width="224" height="224" viewBox="0 0 160 160" style={{ transform: "rotate(-90deg)" }}>
            <circle cx="80" cy="80" r="64" fill="none" stroke="#F1F0EC" strokeWidth="16" />
            <circle
              cx="80"
              cy="80"
              r="64"
              fill="none"
              stroke={band.color}
              strokeWidth="16"
              strokeLinecap="round"
              strokeDasharray={`${(animatedScore / 100) * 2 * Math.PI * 64} ${2 * Math.PI * 64}`}
              style={{ transition: "stroke-dasharray .3s ease" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-display text-6xl font-bold tracking-tight">{animatedScore}</span>
            <span className="text-sm text-mutedLight">/100</span>
          </div>
        </div>
        <span
          className="rounded-full px-4 py-2 text-sm font-bold"
          style={{ background: band.color, color: band.textColor }}
        >
          {band.label}
        </span>
        {activeLevers.length > 0 && (
          <span className="text-sm text-mutedLight">
            Up from a baseline of <strong className="text-ink">{baselineScore}</strong> with nothing fitted.
          </span>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
        <button
          type="button"
          onClick={showBestSetup}
          className="min-h-[56px] rounded-2xl bg-lime px-8 font-display text-lg font-bold tracking-tight text-ink"
        >
          Show me the best setup →
        </button>
        <button
          type="button"
          onClick={reset}
          className="min-h-[56px] rounded-2xl border-2 border-border px-8 font-bold text-ink"
        >
          Reset to baseline
        </button>
      </div>

      <div className="flex flex-col gap-3">
        <div className="text-sm font-bold text-muted">The six pillars</div>
        <ScoreBreakdown pillars={pillars} overall={target} theme="light" showScale={false} />
      </div>

      <div className="flex flex-col gap-3">
        <div className="text-sm font-bold text-muted">Change something, watch it move</div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {LEVERS.map((lever) => {
            const active = activeLevers.includes(lever.key);
            return (
              <button
                key={lever.key}
                type="button"
                onClick={() => toggleLever(lever.key)}
                aria-pressed={active}
                className={`flex min-h-[76px] items-center justify-between gap-4 rounded-2xl border-2 p-4 text-left ${
                  active ? "border-indigo bg-lavender" : "border-border bg-white"
                }`}
              >
                <span>
                  <span className="block font-bold">{lever.label}</span>
                  <span className="block text-xs text-mutedLight">{lever.description}</span>
                </span>
                <span
                  className={`flex h-7 w-[50px] shrink-0 items-center rounded-full p-[3px] transition-colors ${
                    active ? "bg-indigo" : "bg-[#D4D2CB]"
                  }`}
                >
                  <span
                    className={`block h-5 w-5 rounded-full shadow-sm transition-transform ${
                      active ? "translate-x-[22px] bg-white" : "translate-x-0 bg-ink"
                    }`}
                  />
                </span>
              </button>
            );
          })}
        </div>
      </div>

    </div>
  );
}
